import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  createErrorResult,
  currentUserIdSelector,
  DATE_TIME_FORMAT_FILE_NAME,
  errors,
  formatDate,
  getCompanyId,
  IReport,
  IReportApiDto,
  IReportSaveRequestDto,
  IReportSaveResponseDto,
  ISaveReportForm,
  nowTDateISO,
  reportFiltersWithValuesAF,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  ReportOwnerCategoryEnum,
  ReportsApi,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsSortingAF,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidApiResponse,
  useTimezone,
} from "@ryde/shared";

import { useReportId } from "./use-report-id";
import { useReportMenuItems } from "./use-report-menu-items";

export const useSaveReport = () => {
  const { reportId } = useReportId();
  const { t } = useTranslation();

  const currentUserId = useRecoilValue(currentUserIdSelector);
  const { timezone } = useTimezone();
  const { fetchReportMenuItems } = useReportMenuItems();
  const filtersWithValues = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const columnOrder = useRecoilValue(reportsTableColumnsOrderStateAF(reportId));
  const clientColumnSort = useRecoilValue(
    reportsTableColumnsSortingAF(reportId),
  );
  const flexGroupingColumnKeys = useRecoilValue(
    reportFlexGroupingParamsAF(reportId),
  );
  const periodGrouping = useRecoilValue(reportGroupingPeriodAF(reportId));

  const generateDefaultReportName = useEvent(
    (reportDto: IReportApiDto | undefined) => {
      const dateStr = formatDate({
        dateISO: nowTDateISO(timezone),
        dateFormat: DATE_TIME_FORMAT_FILE_NAME,
        timezone,
      });
      const { report, reportTemplate } = reportDto || {};

      if (report?.name) {
        const originalReportName = report.name;
        return `${originalReportName}-${dateStr}`;
      }

      if (reportTemplate?.translationKey) {
        return `${t(reportTemplate.translationKey)}-${dateStr}`;
      }

      return `Report-${dateStr}`;
    },
  );

  const saveReport = useCallback(
    async (
      updatedReport: Partial<ISaveReportForm>,
      reportDto: IReportApiDto,
      isEdit: boolean,
    ): Promise<SimpleResult<IReportSaveResponseDto>> => {
      const report = reportDto.report;
      const companyId = getCompanyId();

      const { ownerCategory, name, description } = updatedReport;

      if (!companyId || !currentUserId) {
        return createErrorResult(errors.common.invalidCompanyId);
      }

      let composedReport: Partial<IReport>;

      if (!ownerCategory) {
        return createErrorResult(errors.reports.invalidReportFormData);
      }

      if (isEdit) {
        composedReport = {
          ...report,
          name: name || report.name,
          description: description,
          companyId:
            ownerCategory !== ReportOwnerCategoryEnum.Global ? null : companyId,
          userId:
            ownerCategory === ReportOwnerCategoryEnum.UserOnly
              ? currentUserId
              : null,
          visibleFilters: filtersWithValues,
          visibleColumns: columnOrder,
          flexGroupingColumnKeys,
          periodGrouping,
          clientColumnSort,
          isUnDeletable: false,
          isSystemProvided: false,
          isActive: true,
        };
      } else {
        composedReport = {
          ...report,
          id: null,
          name: name || report.name,
          translationKey: "",
          descriptionTranslationKey: "",
          description: description,
          companyId:
            ownerCategory === ReportOwnerCategoryEnum.Global ? null : companyId,
          userId:
            ownerCategory === ReportOwnerCategoryEnum.UserOnly
              ? currentUserId
              : null,
          createdAt: nowTDateISO(timezone),
          userCreatorId: currentUserId,
          userUpdaterId: null,
          visibleFilters: filtersWithValues,
          visibleColumns: columnOrder,
          flexGroupingColumnKeys,
          periodGrouping,
          clientColumnSort,
          isUnDeletable: false,
          isSystemProvided: false,
          isActive: true,
        };
      }

      const saveReportApiData: IReportSaveRequestDto = {
        report: composedReport,
      };
      const saveResult = await ReportsApi.addOrUpdate(saveReportApiData);

      if (!saveResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (saveResult.success && !saveResult.data) {
        return simpleResultInvalidApiResponse(t);
      }

      if (saveResult.success) {
        fetchReportMenuItems();
      }

      return saveResult;
    },
    [
      clientColumnSort,
      columnOrder,
      currentUserId,
      fetchReportMenuItems,
      filtersWithValues,
      flexGroupingColumnKeys,
      periodGrouping,
      t,
      timezone,
    ],
  );

  return { generateDefaultReportName, saveReport };
};
